/**
 * The route table.
 *
 * ===========================================================================
 * THE COUNTRY IS IN THE URL, AND THAT IS A DESIGN DECISION
 * ===========================================================================
 * Every in-app path is `/countries/:cc/...`. The alternative — one "active country"
 * held in client state — was rejected: it makes a link unshareable (a colleague
 * opening your URL would land in THEIR country), it makes the browser Back button
 * lie after a country switch, and it means a bug in state management can point a
 * request at the wrong country while the UI still says the old one. With the code in
 * the path, the country a screen is showing and the country it is fetching are the
 * same string, and `RequireCountry` can refuse before anything renders.
 *
 * The route shape:
 *
 *   /login                        public
 *   /countries                    the picker  (skips itself when there is one choice)
 *   /countries/:cc                the dashboard, inside the shell
 *   /countries/:cc/<module>       B4 and later phases (see routes.extra.ts)
 *   *                             everything else
 */

import { Navigate, createBrowserRouter } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { LoginPage } from './auth/LoginPage';
import { CountryPickerPage } from './auth/CountryPickerPage';
import { RequireCountry, RequireSession } from './auth/guards';
import { AppShell } from './components/shell/AppShell';
import { NotFound } from './components/NotFound';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { extraCountryRoutes } from './routes.extra';

export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  {
    element: <RequireSession />,
    children: [
      { path: '/', element: <Navigate to="/countries" replace /> },
      { path: '/countries', element: <CountryPickerPage /> },
      {
        path: '/countries/:cc',
        element: <RequireCountry />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, element: <DashboardPage /> },
              ...extraCountryRoutes,
              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/countries" replace /> },
];

export function createRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(routes);
}
